import {Injectable, isDevMode} from '@angular/core';
import {getRxStorageDexie} from 'rxdb/plugins/storage-dexie';
import {
    addRxPlugin,
    createRxDatabase,
    ExtractDocumentTypeFromTypedRxJsonSchema,
    RxCollection,
    RxDatabase,
    RxJsonSchema,
    toTypedRxJsonSchema,
} from 'rxdb';
import {wrappedValidateAjvStorage} from 'rxdb/plugins/validate-ajv';
import {RxDBQueryBuilderPlugin} from 'rxdb/plugins/query-builder';
import {RxDBDevModePlugin} from 'rxdb/plugins/dev-mode';
import {RxDBMigrationPlugin} from 'rxdb/plugins/migration-schema';
import {State} from '../modules/outage/classes/state';
import {County} from '../modules/outage/classes/county';
import {FemaRegion} from '../modules/outage/classes/fema-region';
import {IRegionOption} from '../interfaces/region-option.interface';
import {firstValueFrom} from 'rxjs';
import {IStateOption} from '../interfaces/state-option.interface';

/**
 * Define all the types, interfaces, and literals necessary to instantiate the DB
 */
export const EI_SCHEMA_LITERAL = {
    title: 'EI Schema',
    description: 'EI represents the necessary geospatial data we wish to preserve',
    version: 0,
    keyCompression: false,
    primaryKey: 'geoType',
    type: 'object',
    properties: {
        geoType: {
            type: 'string',
            default: '',
            maxLength: 100,
        },
        geometries: {},
    },
    required: ['geoType', 'geometries'],
} as const;

const schemaTyped = toTypedRxJsonSchema(EI_SCHEMA_LITERAL);
export type RxEIDocumentType = ExtractDocumentTypeFromTypedRxJsonSchema<typeof schemaTyped>;

export const EI_SCHEMA: RxJsonSchema<RxEIDocumentType> = EI_SCHEMA_LITERAL;

export type RxEICollection = RxCollection<RxEIDocumentType>;

export interface RxEICollections {
    eaglei: RxEICollection;
}

export type RxEIDatabase = RxDatabase<RxEICollections>;
export interface EIindexDBCollection {
    geoType: string;
    geometries: State[] | County[] | FemaRegion[] | Map<number, State[]> | IRegionOption[] | IStateOption[];
}

export interface RxEIDatabaseResponse {
    [key: string]: State[] | County[] | FemaRegion[] | Map<number, State[]> | IRegionOption[] | IStateOption[];
}

/**
 * Save for later. If/when we need to do a migration we can define them here
 */
// const dbMigrations = {
//     1: function (
//         oldDoc: RxDocument<{
//             type: string;
//             geometries: any;
//         }>
//     ) {
//         return oldDoc;
//     },
// };

const eiCollectionSettings = {
    eaglei: {
        schema: EI_SCHEMA,
        methods: {},
        sync: true,
        migrationStrategies: {}, // if we use migrations change this to dbMigrations
    },
};

@Injectable({
    providedIn: 'root',
})
export class IndexDbService {
    private db!: RxDatabase<RxEIDatabase, any, any>;

    /**
     * The primary way to interact with the db
     *
     * @returns Promise<RxEICollection>
     */
    public async eiCollection() {
        return this.dbIsReady();
    }

    /**
     * If we try to query the DB before its established we run into some annoying errors.
     *
     * This way, if its not established we return the DB _after_ it is established,
     * otherwise return the created instance
     *
     * @returns Promise<RxEIDatabase>
     */
    private async dbIsReady(): Promise<RxEICollection> {
        if (this.db) {
            return this.db.collections.eaglei;
        }

        if (isDevMode()) {
            addRxPlugin(RxDBDevModePlugin);
        }

        addRxPlugin(RxDBQueryBuilderPlugin);
        addRxPlugin(RxDBMigrationPlugin);

        this.db = await createRxDatabase<RxEIDatabase>({
            name: 'eaglei',
            storage: wrappedValidateAjvStorage({
                storage: getRxStorageDexie(),
            }),
            ignoreDuplicate: true,
            multiInstance: true,
        });

        await this.db.addCollections(eiCollectionSettings);

        return this.db.collections.eaglei;
    }

    /**
     *
     * @param ids string[]
     * @returns
     *
     * Querying IndexedDB and formatting the response has is a number of hoops to jump through so we
     * "simplify" it here
     */
    public async simpleQueryByIds(ids: string[]): Promise<RxEIDatabaseResponse> {
        // Get the db instance
        const db = await this.eiCollection();
        // convert query response to a promise
        const documents = await firstValueFrom(db.findByIds(ids).$);
        const results: RxEIDatabaseResponse = {};

        // Go through each document and format the data
        documents.forEach((document) => {
            const doc = document.toMutableJSON(true) as EIindexDBCollection;
            results[doc.geoType] = doc.geometries;
        });

        return results;
    }
}
